import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { motion } from "framer-motion";

import * as Common from "../commons/common";
import * as Request from "../commons/request";
import * as TypeDTO from "../commons/typeDTO";
import * as Utils from "../utils/utils";

import PageHeader, { TypeHeader } from "../components/PageHeader";
import { useAsyncAxios, utilAxiosWithAppToken, utilAxiosWithAuth } from "../utils/customAxios";

import ModalBatteryState from "../components/ModalBatteryState";
import ModalSubBatteryState from "../components/ModalSubBatteryState";
import ModalDoorState from "../components/ModalDoorState";

import styles from "./PageLayout.module.css";
import ModalCarState from "../components/ModalCarState";
import { useAuthDispatch, useAuthState } from "../provider/AuthProvider";

declare const window: typeof globalThis & {
    Android: any;
};

const pageVariants = {
    squish: {
        opacity: 0,
        x: "-10vw",
    },
    appear: {
        opacity: 1,
        x: "0",
        transition: { duration: 0.15 },
    },

    exit: {
        opacity: 0,
        transition: { ease: "easeIn", duration: 0.15 },
    },
};

function PageLayout({ children, headerType }: { children: React.ReactNode; headerType?: TypeHeader }) {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAuthDispatch();
    const userDetails = useAuthState();

    useEffect(() => {
        window.addEventListener(Common.PUSH_NOTIFICATION_BATTERY, onBatteryPushNotification);
        window.addEventListener(Common.PUSH_NOTIFICATION_SUB_BATTERY, onSubBatteryPushNotification);
        window.addEventListener(Common.PUSH_NOTIFICATION_DOOR_STATE, onDoorPushNotification);
        window.addEventListener(Common.PUSH_NOTIFICATION_CAR_STATE, onCarPushNotification);

        window.addEventListener(Common.EXTERNAL_EVENT_BACK, onBackPressEvent);
        window.addEventListener(Common.EXTERNAL_EVENT_RESUME, onResumeAppEvent);

        Utils.onEvent(Common.EVENT_BATTERY, onBatteryEvent);
        Utils.onEvent(Common.EVENT_RE_LOGIN, onReLogin);

        return () => {
            window.removeEventListener(Common.PUSH_NOTIFICATION_BATTERY, onBatteryPushNotification);
            window.removeEventListener(Common.PUSH_NOTIFICATION_SUB_BATTERY, onSubBatteryPushNotification);
            window.removeEventListener(Common.PUSH_NOTIFICATION_DOOR_STATE, onDoorPushNotification);
            window.removeEventListener(Common.PUSH_NOTIFICATION_CAR_STATE, onCarPushNotification);

            window.removeEventListener(Common.EXTERNAL_EVENT_BACK, onBackPressEvent);
            window.removeEventListener(Common.EXTERNAL_EVENT_RESUME, onResumeAppEvent);

            Utils.offEvent(Common.EVENT_BATTERY, onBatteryEvent);
            Utils.offEvent(Common.EVENT_RE_LOGIN, onReLogin);
        };
    }, [location]);

    const onBatteryPushNotification = async (event: any) => {
        console.log("onBatteryPushNotification title", event.detail.title);
        console.log("onBatteryPushNotification body", event.detail.body);
        console.log("onBatteryPushNotification battery", event.detail.battery);

        showModalBattery(event.detail.battery);
    };
    const onSubBatteryPushNotification = async (event: any) => {
        console.log("onSubBatteryPushNotification title", event.detail.title);
        console.log("onSubBatteryPushNotification body", event.detail.body);
        console.log("onSubBatteryPushNotification subBattery", event.detail.subBattery);

        showModalSubBattery(event.detail.subBattery);
    };

    const onDoorPushNotification = async (event: any) => {
        console.log("onDoorPushNotification title", event.detail.title);
        console.log("onDoorPushNotification body", event.detail.body);
        console.log("onDoorPushNotification doorStatus", event.detail.doorStatus);

        showModalDoorState(event.detail.doorStatus);
    };

    const onCarPushNotification = async (event: any) => {
        console.log("onCarPushNotification title", event.detail.title);
        console.log("onCarPushNotification body", event.detail.body);
        console.log("onCarPushNotification keyOffStatus", event.detail.keyOffStatus);

        showModalCarState(event.detail.keyOffStatus);
    };

    const onBackPressEvent = async () => {
        // console.log("onBackPressEvent", location.pathname);
        if (Utils.getModalStack() !== 0) {
            setOpenModalBatteryState(false);
            setOpenModalSubBatteryState(false);
            setOpenModalDoorState(false);

            Utils.triggerEvent(Common.EVENT_CLOSE_ALL_MODAL);
            Utils.initModalStack();
        } else if (Utils.getDrawerStack() !== 0) {
            Utils.triggerEvent(Common.EVENT_CLOSE_ALL_DRAWER);
            Utils.initDrawerStack();
        } else {
            const path = location.pathname;
            switch (path) {
                case Common.PAGE_MAIN:
                    window.Android.closeAppOnBackPress();
                    break;
                default:
                    navigate(-1);
            }
        }
    };

    const [myCars, setMyCars] = useState<Array<TypeDTO.CarOwnerDto>>();
    const [activeMyCars, setActiveMyCars] = useState<TypeDTO.CarOwnerDto>();

    const requestAxiosGetMyCars = async () => {
        const response = await utilAxiosWithAuth().get(Request.OWNERS_MINE_URL);
        return response.data;
    };

    const {
        loading: loadingGetMyCars,
        error: errorGetMyCars,
        data: resultGetMyCars,
        execute: requestGetMyCars,
    } = useAsyncAxios(requestAxiosGetMyCars);

    useEffect(() => {
        if (resultGetMyCars === null) return;
        console.log("resultGetMyCars", resultGetMyCars);

        // TEST Code : setAllBatteryStatus(15, "VERY_LOW");

        setMyCars(resultGetMyCars.carOwners);
    }, [resultGetMyCars]);

    useEffect(() => {
        if (errorGetMyCars === null) return;

        console.log("errorGetMyCars", errorGetMyCars);
    }, [errorGetMyCars]);

    useEffect(() => {
        if (myCars === null || myCars === undefined || myCars.length === 0) {
            setActiveMyCars(undefined);
        } else {
            const activeCars = myCars.filter((car) => car.active === true);
            if (activeCars && activeCars.length > 0) {
                setActiveMyCars(activeCars[0]);
            } else setActiveMyCars(undefined);
        }
    }, [myCars]);

    useEffect(() => {
        if (activeMyCars !== undefined && activeMyCars.car && activeMyCars.car.batterySoc && activeMyCars.car.subBatteryStatus) {
            setAllBatteryStatus(activeMyCars.car.batterySoc, activeMyCars.car.subBatteryStatus);
        }
    }, [activeMyCars]);

    const onResumeAppEvent = async () => {
        console.log("onResumeAppEvent");

        userDetails?.user && requestGetMyCars();
    };

    const onReLogin = async () => {
        console.log("onReLogin");
        const result = Utils.isPossibleAutoLogin();
        if (result.isPossible && result.email && result.password) {
            relogin({ email: result.email, password: result.password });
        }
    };

    const requestLogin = async (loginInfo: Common.TypeLogin) => {
        const response = await utilAxiosWithAppToken().post(Request.LOGIN_URL, loginInfo);
        return response.data;
    };

    const { loading, error: errorLogin, data: resultLogin, execute: relogin } = useAsyncAxios(requestLogin);

    useEffect(() => {
        if (resultLogin === null) return;

        if (dispatch !== null) {
            dispatch({
                type: "LOGIN_SUCCESS",
                payload: {
                    user: resultLogin.user,
                    token: resultLogin.token,
                },
            });
        }

        Utils.sendFCMToken();
        window.location.reload();
    }, [resultLogin]);

    useEffect(() => {
        if (errorLogin === null) {
            return;
        }
        navigate(Common.PAGE_LOGIN, { replace: true });
    }, [errorLogin]);

    const onBatteryEvent = async (event: any) => {
        setAllBatteryStatus(event.detail.battery, event.detail.subBattery);
    };

    const showModalBattery = (battery: number) => {
        if (battery <= 40) {
            setBatteryState(battery);
            setOpenModalBatteryState(true);
        }
    };

    const showModalSubBattery = (subBattery: string) => {
        /*
        if (subBattery.toUpperCase() === "LOW" || subBattery.toUpperCase() === "VERY_LOW") {
            setSubBatteryState(subBattery);
            setOpenModalSubBatteryState(true);
        }
        */
    };

    const showModalDoorState = (doorStatus: string) => {
        if (
            doorStatus.toUpperCase() === "OPEN" ||
            doorStatus.toUpperCase() === "CLOSED" ||
            doorStatus.toUpperCase() === "FAIL_NOT_P" ||
            doorStatus.toUpperCase() === "S_OPEN" ||
            doorStatus.toUpperCase() === "S_CLOSED" ||
            doorStatus.toUpperCase() === "S_FAIL_NOT_P"
        ) {
            setDoorState(doorStatus);
            setOpenModalDoorState(true);
        }
    };

    const showModalCarState = (keyOffStatus: string) => {
        if (keyOffStatus.toUpperCase() === "KEYOFF_NOT_P") {
            setCarState(keyOffStatus);
            setOpenModalCarState(true);
        }
    };

    const setAllBatteryStatus = (battery: number, subBattery: string) => {
        showModalBattery(battery);
        showModalSubBattery(subBattery);
    };

    const [batteryState, setBatteryState] = useState<number>();
    const [openModalBatteryState, setOpenModalBatteryState] = useState(false);

    // useEffect(() => {
    //     if (batteryState === undefined) return;

    //     if (batteryState <= 40) setOpenModalBatteryState(true);
    // }, [batteryState]);

    const [subBatteryState, setSubBatteryState] = useState<string>();
    const [openModalSubBatteryState, setOpenModalSubBatteryState] = useState(false);

    // useEffect(() => {
    //     if (subBatteryState === undefined) return;

    //     setOpenModalSubBatteryState(true);
    // }, [subBatteryState]);

    const [doorState, setDoorState] = useState<string>();
    const [openModalDoorState, setOpenModalDoorState] = useState(false);

    // useEffect(() => {
    //     if (doorState === undefined) return;

    //     setOpenModalDoorState(true);
    // }, [doorState]);

    const [carState, setCarState] = useState<string>();
    const [openModalCarState, setOpenModalCarState] = useState(false);

    return (
        <div className={styles.container}>
            <PageHeader headerType={headerType} myCar={activeMyCars?.car} />
            <motion.div variants={pageVariants} initial="squish" animate="appear" exit="exit">
                {children}
            </motion.div>

            <ModalBatteryState
                batteryLevel={batteryState}
                openModalBatteryState={openModalBatteryState}
                onChangedOpenModalBatteryState={(opened) => setOpenModalBatteryState(opened)}
            />

            <ModalSubBatteryState
                subBatteryState={subBatteryState}
                openModalSubBatteryState={openModalSubBatteryState}
                onChangedOpenModalSubBatteryState={(opened) => setOpenModalSubBatteryState(opened)}
            />

            <ModalDoorState
                doorState={doorState}
                openModalDoorState={openModalDoorState}
                onChangedOpenModalDoorState={(opened) => setOpenModalDoorState(opened)}
            />

            <ModalCarState
                carState={carState}
                openModalCarState={openModalCarState}
                onChangedOpenModalCarState={(opened) => setOpenModalCarState(opened)}
            />
        </div>
    );
}

export default PageLayout;
